import React, { useEffect, useState, lazy } from 'react';
import { useFadeIn } from '../hooks/useFadeIn';
import { ClockIcon, AlertCircleIcon, CheckIcon } from 'lucide-react';
const CHECKOUT_URLS = {
  base: 'https://recursopack.com/cart/40898068152369:1',
  familia: 'https://recursopack.com/cart/41028087676977:1',
  pareja: 'https://recursopack.com/cart/41028087775281:1',
  both: 'https://recursopack.com/cart/40916383236145:1'
};
const BASE_PRICE = 17;
const BUMP_PRICE = 6;
const ORDER_BUMPS = [{
  id: 'familia',
  title: '100 Citas en Familia',
  emoji: '👨‍👩‍👧‍👦',
  image: 'https://cdn.magicpatterns.com/uploads/6ahqC8DtZLBqxcRbZSRbKG/mocap_familia.webp',
  review: '100 momentos para reír, jugar y crear recuerdos que nunca se borran. ❤️',
  checkoutUrl: '#' // TODO: user will provide
}, {
  id: 'pareja',
  title: '100 Citas en Pareja',
  emoji: '💞',
  image: 'https://cdn.magicpatterns.com/uploads/sBkdaRUBkxxy4BWCaasZ24/mocap_pareja.webp',
  review: 'Reavivá la chispa con 100 planes para volver a elegirse cada día. 💞',
  checkoutUrl: '#' // TODO: user will provide
}];
export function PricingSection() {
  const fadeIn = useFadeIn();
  const [timeLeft, setTimeLeft] = useState(85408);
  const [selectedBumps, setSelectedBumps] = useState<string[]>([]);
  useEffect(() => {
    const startDelay = setTimeout(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
      const timer = setInterval(() => {
        setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
      }, 1000);
      return () => clearInterval(timer);
    }, 3000);
    return () => clearTimeout(startDelay);
  }, []);
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor(timeLeft % 3600 / 60);
  const seconds = timeLeft % 60;
  const books = [{
    emoji: '📘',
    text: '100 Citas con mi Hija (versión mamá e hija)'
  }, {
    emoji: '📗',
    text: '100 Citas con mi Hijo (versión mamá e hijo)'
  }, {
    emoji: '📙',
    text: '100 Citas con mi Hija (versión papá e hija)'
  }, {
    emoji: '📕',
    text: '100 Citas con mi Hijo (versión papá e hijo)'
  }];
  const bookFeatures = ['Actividades y desafíos familiares simples, económicos y divertidos', 'Momentos guiados para fortalecer la conexión emocional', 'Ideas para desconectarse de las pantallas y reconectarse entre ustedes', 'Espacios para escribir, reflexionar y guardar recuerdos'];
  const bonuses = [{
    emoji: '💌',
    text: 'Recuerdos para mi hija — 30 mensajes y reflexiones que ella recordará toda la vida'
  }, {
    emoji: '🎲',
    text: 'Adiós al aburrimiento — 52 planes económicos y entretenidos para disfrutar todo el año'
  }, {
    emoji: '🔑',
    text: 'Juegos secretos — Actividades que revelan lo que tus hijos realmente piensan y sienten'
  }, {
    emoji: '👵',
    text: 'Abuelita, cuéntame tu historia — Un viaje emocional por sus recuerdos'
  }, {
    emoji: '👴',
    text: 'Abuelito, cuéntame tu historia — Un legado familiar lleno de anécdotas'
  }, {
    emoji: '👩‍👧',
    text: 'Mamá, cuéntame tu historia — Un espacio íntimo para compartir experiencias'
  }, {
    emoji: '👨‍👦',
    text: 'Papá, cuéntame tu historia — Momentos de conexión profunda entre padre e hijo'
  }];
  const toggleBump = (id: string) => {
    setSelectedBumps(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]);
  };
  const total = BASE_PRICE + selectedBumps.length * BUMP_PRICE;
  const totalDisplay = total.toFixed(2).replace('.', ',');
  const savedAmount = selectedBumps.length * 4; // €10 crossed out vs €6 = €4 savings each
  const hasFamilia = selectedBumps.includes('familia');
  const hasPareja = selectedBumps.includes('pareja');
  const checkoutUrl = hasFamilia && hasPareja ? CHECKOUT_URLS.both : hasFamilia ? CHECKOUT_URLS.familia : hasPareja ? CHECKOUT_URLS.pareja : CHECKOUT_URLS.base;
  return <section id="pricing" className="py-10 md:py-20 px-4 bg-[#111111] scroll-mt-10" data-id="element-162">
      <div className="max-w-3xl mx-auto" data-id="element-163">
        <div ref={fadeIn.ref} className={fadeIn.className} data-id="element-164">
          {/* Countdown Block */}
          <div className="text-center mb-8 md:mb-10" data-id="element-165">
            <div className="inline-flex items-center gap-2 bg-red-900/60 border border-red-500/40 text-red-300 font-bold text-xs md:text-base px-4 py-2 md:px-5 md:py-2 rounded-full mb-5 md:mb-6" data-id="element-166">
              <ClockIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-167" />
              <span data-id="element-168">OFERTA TERMINA EN:</span>
            </div>

            <div className="flex items-center justify-center gap-2 md:gap-3 mb-5 md:mb-6" data-id="element-169">
              <div className="bg-[#1a1a2e] border border-gray-700 rounded-xl px-3 py-2 md:px-6 md:py-4 min-w-[70px] md:min-w-[90px]" data-id="element-170">
                <div className="text-2xl md:text-5xl font-black text-white" data-id="element-171">
                  {hours.toString().padStart(2, '0')}
                </div>
                <div className="text-[10px] md:text-sm text-gray-500 font-medium mt-1" data-id="element-172">
                  hs
                </div>
              </div>
              <span className="text-xl md:text-4xl font-black text-red-400" data-id="element-173">
                :
              </span>
              <div className="bg-[#1a1a2e] border border-gray-700 rounded-xl px-3 py-2 md:px-6 md:py-4 min-w-[70px] md:min-w-[90px]" data-id="element-174">
                <div className="text-2xl md:text-5xl font-black text-white" data-id="element-175">
                  {minutes.toString().padStart(2, '0')}
                </div>
                <div className="text-[10px] md:text-sm text-gray-500 font-medium mt-1" data-id="element-176">
                  min
                </div>
              </div>
              <span className="text-xl md:text-4xl font-black text-red-400" data-id="element-177">
                :
              </span>
              <div className="bg-[#1a1a2e] border border-gray-700 rounded-xl px-3 py-2 md:px-6 md:py-4 min-w-[70px] md:min-w-[90px]" data-id="element-178">
                <div className="text-2xl md:text-5xl font-black text-white" data-id="element-179">
                  {seconds.toString().padStart(2, '0')}
                </div>
                <div className="text-[10px] md:text-sm text-gray-500 font-medium mt-1" data-id="element-180">
                  seg
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 text-yellow-400 font-bold text-xs md:text-base" data-id="element-181">
              <AlertCircleIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-182" />
              <span data-id="element-183">Después sube a su precio normal. No vuelve.</span>
            </div>
          </div>

          {/* Single Offer Card */}
          <div className="relative bg-gradient-to-b from-[#0d0d1a] to-[#111122] border-2 border-yellow-400 rounded-3xl p-6 md:p-10 shadow-2xl" data-id="element-184">
            {/* Limited time ribbon */}
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-10" data-id="element-185">
              <span className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white text-sm md:text-base font-black px-5 md:px-7 py-2 md:py-2.5 rounded-full uppercase tracking-wider shadow-xl whitespace-nowrap" data-id="element-186">
                🎉 OFERTA POR TIEMPO LIMITADO
              </span>
            </div>

            {/* Intro */}
            <div className="mt-4 mb-6 md:mb-8 text-center" data-id="element-187">
              <p className="text-base md:text-xl text-gray-300 font-medium leading-relaxed" data-id="element-188">
                ✅ Estás a un paso de acceder al{' '}
                <span className="text-yellow-400 font-black" data-id="element-189">
                  Pack 100 Citas Familiares
                </span>
                , un conjunto único de libros digitales diseñados para
                fortalecer la conexión con tus hijos y toda tu familia.
              </p>
            </div>

            {/* What's included */}
            <div className="mb-6 md:mb-8" data-id="element-190">
              <h3 className="text-lg md:text-2xl font-black text-white mb-4 md:mb-5 text-center" data-id="element-191">
                🎁 Te llevás todo esto:
              </h3>

              <p className="text-sm md:text-base text-yellow-300 font-bold mb-3 md:mb-4" data-id="element-192">
                4 Libros Digitales con actividades, juegos y momentos únicos:
              </p>
              <div className="grid grid-cols-1 gap-2.5 mb-5 md:mb-6" data-id="element-193">
                {books.map((b, i) => <div key={i} className="flex items-start gap-3 bg-[#1a1a2e] border border-gray-700/50 rounded-xl p-3 md:p-3.5" data-id="element-194">
                    <span className="text-xl md:text-2xl flex-shrink-0" data-id="element-195">
                      {b.emoji}
                    </span>
                    <p className="text-sm md:text-base text-gray-300 font-medium" data-id="element-196">
                      {b.text}
                    </p>
                  </div>)}
              </div>

              <p className="text-sm md:text-base text-blue-300 font-bold mb-2 md:mb-3" data-id="element-197">
                ✨ Cada libro incluye:
              </p>
              <ul className="space-y-1.5 md:space-y-2 mb-5 md:mb-6 pl-2" data-id="element-198">
                {bookFeatures.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm md:text-base text-gray-400" data-id="element-199">
                    <span className="text-green-400 mt-0.5 flex-shrink-0" data-id="element-200">
                      ✓
                    </span>
                    <span data-id="element-201">{f}</span>
                  </li>)}
              </ul>

              <p className="text-sm md:text-base text-yellow-300 font-bold mb-3 md:mb-4" data-id="element-202">
                🎁 Además, te llevás 7 bonos exclusivos:
              </p>
              <div className="grid grid-cols-1 gap-2.5" data-id="element-203">
                {bonuses.map((b, i) => <div key={i} className="flex items-start gap-3 bg-[#1a1a2e] border border-gray-700/50 rounded-xl p-3" data-id="element-204">
                    <span className="text-lg md:text-xl flex-shrink-0" data-id="element-205">
                      {b.emoji}
                    </span>
                    <p className="text-xs md:text-sm text-gray-300 font-medium" data-id="element-206">
                      {b.text}
                    </p>
                  </div>)}
              </div>
            </div>

            {/* Price block */}
            <div className="bg-[#0a0a14] border-2 border-yellow-400/50 rounded-2xl p-5 md:p-8 mb-6 md:mb-8 text-center" data-id="element-207">
              <p className="text-sm md:text-base text-gray-400 line-through mb-2" data-id="element-208">
                ¡Antes €37.00 EUR!
              </p>
              <p className="inline-block bg-red-600 text-white text-xs md:text-sm font-black px-3 py-1 rounded-full uppercase tracking-wider mb-3" data-id="element-209">
                SOLO POR HOY
              </p>
              <div className="flex items-baseline justify-center gap-2 mb-2" data-id="element-210">
                <span className="text-sm md:text-base text-gray-400 font-medium" data-id="element-211">
                  EUR
                </span>
                <span className="text-5xl md:text-7xl font-black text-yellow-400" data-id="element-212">
                  €{totalDisplay}
                </span>
              </div>
              {selectedBumps.length > 0 && <p className="text-xs md:text-sm text-green-400 font-bold mb-2" data-id="element-213">
                  🎉 ¡Ahorrás €{savedAmount.toFixed(2).replace('.', ',')} con
                  los bonos agregados!
                </p>}
              <p className="text-xs md:text-sm text-gray-400 font-medium" data-id="element-214">
                🇪🇸 ¡Pago seguro en euros!
              </p>
            </div>

            {/* Order Bumps */}
            <div className="mb-6 md:mb-8" data-id="element-215">
              <p className="text-center text-xs md:text-sm font-black text-yellow-400 uppercase tracking-widest mb-4" data-id="element-216">
                ✨ Agregá a tu pedido y ahorrá:
              </p>
              <div className="space-y-3" data-id="element-217">
                {ORDER_BUMPS.map(bump => {
                const isSelected = selectedBumps.includes(bump.id);
                return <button key={bump.id} type="button" onClick={() => toggleBump(bump.id)} className={`w-full text-left rounded-2xl border-2 transition-all overflow-hidden ${isSelected ? 'border-yellow-400 bg-yellow-400/5' : 'border-gray-700/60 bg-[#1a1a2e] hover:border-yellow-400/50'}`} data-id="element-218">
                      {/* Top ribbon */}
                      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white text-[10px] md:text-xs font-black uppercase tracking-wider py-1.5 text-center" data-id="element-219">
                        ⭐ Altamente recomendado
                      </div>
                      <div className="p-3 md:p-4 flex items-start gap-3" data-id="element-220">
                        {/* Checkbox */}
                        <div className={`mt-1 flex-shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-md border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-yellow-400 border-yellow-400' : 'border-gray-500 bg-transparent'}`} data-id="element-221">
                          {isSelected && <CheckIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-black" strokeWidth={3} data-id="element-222" />}
                        </div>
                        {/* Image */}
                        <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 bg-gray-900 rounded-lg overflow-hidden flex items-center justify-center" data-id="element-223">
                          <img src={bump.image} alt={bump.title} className="w-full h-full object-contain" loading="lazy" decoding="async" width={200} height={200} data-id="element-224" />
                        </div>
                        {/* Content */}
                        <div className="flex-1 min-w-0" data-id="element-225">
                          <p className="text-sm md:text-base font-black text-white leading-tight mb-1" data-id="element-226">
                            {bump.title} <span data-id="element-227">{bump.emoji}</span>
                          </p>
                          <p className="text-sm md:text-base font-bold mb-1.5" data-id="element-228">
                            <span className="text-yellow-400" data-id="element-229">+ €6,00</span>{' '}
                            <span className="text-gray-500 line-through text-xs md:text-sm font-medium" data-id="element-230">
                              €10,00
                            </span>
                          </p>
                          <p className="text-xs md:text-sm text-gray-400 italic leading-snug" data-id="element-231">
                            {bump.review}
                          </p>
                        </div>
                      </div>
                    </button>;
              })}
              </div>
            </div>

            {/* CTA */}
            <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="block w-full" onClick={() => {
            const w = window as any;
            if (w.fbq) {
              w.fbq('track', 'InitiateCheckout', {
                value: total,
                currency: 'EUR',
                num_items: 1 + selectedBumps.length
              });
            }
          }} data-id="element-232">
              <div className="relative w-full group cta-pulse" data-id="element-233">
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl opacity-70 md:group-hover:opacity-100 transition duration-200" data-id="element-234"></div>
                <div className="relative w-full text-center font-black text-base md:text-2xl uppercase tracking-wide py-4 md:py-5 rounded-xl bg-gradient-to-b from-yellow-400 to-yellow-500 border-b-4 border-yellow-700 text-black shadow-2xl" data-id="element-235">
                  👉 SÍ, QUIERO MI PACK — €{totalDisplay}
                </div>
              </div>
            </a>

            <div className="flex flex-col items-center justify-center gap-2 mt-5 text-gray-500 text-xs md:text-sm" data-id="element-236">
              <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4" data-id="element-237">
                <span data-id="element-238">🔒 Pago 100% seguro</span>
                <span data-id="element-239">•</span>
                <span data-id="element-240">🛡️ Garantía 7 días</span>
              </div>
              <span data-id="element-241">🇪🇸 Pago en euros — Tarjeta de crédito, débito y más</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}